// src/auth/AuthProvider.tsx
import React, {
  createContext,
  useContext,
  useEffect,
  useMemo,
  useState,
  useCallback,
  ReactNode,
} from 'react';
import environment from '../environment';
import { setActiveAuthUser } from '../utils/global/getPermission';
import {
  AuthState,
  AuthUser,
  AuthContextType,
  LoginInput,
  LoginResponse,
  AUTH_STORAGE_KEYS,
} from './types';

const initialState: AuthState = {
  isAuthenticated: false,
  isLoading: true,
  user: null,
  accessToken: null,
  expiresAt: null,
  error: null,
};

const AuthContext = createContext<AuthContextType | undefined>(undefined);

const isDebugLoggingEnabled = process.env.NODE_ENV !== 'production';

const debugLog = (...message: unknown[]) => {
  if (isDebugLoggingEnabled) {
    console.debug('[AuthProvider]', ...message);
  }
};

// Storage helpers
const saveToStorage = (token: string, user: AuthUser, expiresAt: number) => {
  localStorage.setItem(AUTH_STORAGE_KEYS.ACCESS_TOKEN, token);
  localStorage.setItem(AUTH_STORAGE_KEYS.USER, JSON.stringify(user));
  localStorage.setItem(AUTH_STORAGE_KEYS.EXPIRES_AT, expiresAt.toString());
};

const clearStorage = () => {
  localStorage.removeItem(AUTH_STORAGE_KEYS.ACCESS_TOKEN);
  localStorage.removeItem(AUTH_STORAGE_KEYS.USER);
  localStorage.removeItem(AUTH_STORAGE_KEYS.EXPIRES_AT);
  // Also clear the selected customer
  localStorage.removeItem('selectedCustomer');
  localStorage.removeItem('selectedCustomerName');
};

const loadFromStorage = (): {
  token: string | null;
  user: AuthUser | null;
  expiresAt: number | null;
} => {
  const token = localStorage.getItem(AUTH_STORAGE_KEYS.ACCESS_TOKEN);
  const userStr = localStorage.getItem(AUTH_STORAGE_KEYS.USER);
  const expiresAtStr = localStorage.getItem(AUTH_STORAGE_KEYS.EXPIRES_AT);

  let user: AuthUser | null = null;
  if (userStr) {
    try {
      user = JSON.parse(userStr);
    } catch (e) {
      console.error('Failed to parse stored user:', e);
    }
  }

  const expiresAt = expiresAtStr ? parseInt(expiresAtStr, 10) : null;

  return { token, user, expiresAt };
};

interface IAuthProviderProps {
  children: ReactNode;
  LoadingComponent?: ReactNode;
}

export const AuthProvider: React.FC<IAuthProviderProps> = ({ children, LoadingComponent }) => {
  const [state, setState] = useState<AuthState>(initialState);

  // Check for existing session on mount
  useEffect(() => {
    const { token, user, expiresAt } = loadFromStorage();

    if (token && user && expiresAt) {
      // Check if token is expired
      if (Date.now() < expiresAt) {
        debugLog('Restored session from storage');
        setActiveAuthUser(user);
        setState({
          isAuthenticated: true,
          isLoading: false,
          user,
          accessToken: token,
          expiresAt,
          error: null,
        });
      } else {
        debugLog('Stored token is expired, clearing');
        clearStorage();
        setActiveAuthUser(null);
        setState({
          ...initialState,
          isLoading: false,
        });
      }
    } else {
      setActiveAuthUser(null);
      setState({
        ...initialState,
        isLoading: false,
      });
    }
  }, []);

  // Keep active auth user in sync with state
  useEffect(() => {
    setActiveAuthUser(state.user);
  }, [state.user]);

  // Set up token expiration check
  useEffect(() => {
    if (!state.expiresAt || !state.isAuthenticated) return;

    const timeUntilExpiry = state.expiresAt - Date.now();

    if (timeUntilExpiry <= 0) {
      debugLog('Token expired, logging out');
      clearStorage();
      setState({
        ...initialState,
        isLoading: false,
        error: 'Session expired. Please log in again.',
      });
      return;
    }

    // Set timeout to logout when token expires
    const timeout = setTimeout(() => {
      debugLog('Token expiration timeout triggered');
      clearStorage();
      setState({
        ...initialState,
        isLoading: false,
        error: 'Session expired. Please log in again.',
      });
    }, timeUntilExpiry);

    return () => {
      clearTimeout(timeout);
    };
  }, [state.expiresAt, state.isAuthenticated]);

  // Helper function to handle login response
  const handleLoginResponse = useCallback((loginData: LoginResponse) => {
    const expiresAt = Date.now() + loginData.expiresIn * 1000;
    debugLog('Login successful for:', loginData.user.displayName);
    saveToStorage(loginData.accessToken, loginData.user, expiresAt);
    setState({
      isAuthenticated: true,
      isLoading: false,
      user: loginData.user,
      accessToken: loginData.accessToken,
      expiresAt,
      error: null,
    });
  }, []);

  // Helper function to execute GraphQL mutation
  const executeGraphQLMutation = useCallback(
    async (query: string, variables: Record<string, unknown>) => {
      const apiUrl = `${environment.API_URL}/graphql`;
      const response = await fetch(apiUrl, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ query, variables }),
      });

      if (!response.ok) {
        const errorText = await response.text();
        debugLog('HTTP error:', response.status, errorText);
        throw new Error(`Server error: ${response.status} ${response.statusText}`);
      }

      const result = await response.json();

      if (result.errors && result.errors.length > 0) {
        const errorMessage = result.errors[0].message || 'Operation failed';
        debugLog('GraphQL error:', errorMessage);
        throw new Error(errorMessage);
      }

      return result.data;
    },
    [],
  );

  const login = useCallback(
    async (credentials: LoginInput) => {
      debugLog('Attempting login for:', credentials.username);

      setState((prev) => {
        return {
          ...prev,
          isLoading: true,
          error: null,
        };
      });

      try {
        const data = await executeGraphQLMutation(
          `
          mutation Login($input: LoginInput!) {
            login(input: $input) {
              accessToken
              expiresIn
              user {
                id
                loginName
                displayName
                firstName
                lastName
                email
                role
                customerIds
                locale
              }
            }
          }
        `,
          { input: credentials },
        );

        if (!data?.login) {
          throw new Error('Invalid response from server');
        }

        handleLoginResponse(data.login);
      } catch (error) {
        debugLog('Login catch error:', error);

        let message = 'An unexpected error occurred';

        if (error instanceof TypeError && error.message === 'Failed to fetch') {
          message = `Cannot connect to server. Please check if the backend is running.`;
        } else if (error instanceof Error) {
          message = error.message;
        }

        setState((prev) => {
          if (prev.isLoading) {
            return {
              ...prev,
              isLoading: false,
              error: message,
            };
          }
          return prev;
        });

        throw error;
      }
    },
    [executeGraphQLMutation, handleLoginResponse],
  );

  const loginWithProvider = useCallback(
    async (provider: 'AZURE' | 'GOOGLE', idToken: string) => {
      debugLog(`Attempting login with provider: ${provider}`);

      setState((prev) => {
        return {
          ...prev,
          isLoading: true,
          error: null,
        };
      });

      try {
        const data = await executeGraphQLMutation(
          `
          mutation LoginWithProvider($provider: String!, $idToken: String!) {
            loginWithProvider(provider: $provider, idToken: $idToken) {
              accessToken
              expiresIn
              user {
                id
                loginName
                displayName
                firstName
                lastName
                email
                role
                customerIds
                locale
              }
            }
          }
        `,
          { provider, idToken },
        );

        if (!data?.loginWithProvider) {
          throw new Error('Invalid response from server');
        }

        handleLoginResponse(data.loginWithProvider);
      } catch (error) {
        debugLog('Provider login catch error:', error);

        let message = 'An unexpected error occurred';

        if (error instanceof TypeError && error.message === 'Failed to fetch') {
          message = `Cannot connect to server. Please check if the backend is running.`;
        } else if (error instanceof Error) {
          message = error.message;
        }

        setState((prev) => {
          if (prev.isLoading) {
            return {
              ...prev,
              isLoading: false,
              error: message,
            };
          }
          return prev;
        });

        throw error;
      }
    },
    [executeGraphQLMutation, handleLoginResponse],
  );

  const requestMagicLink = useCallback(
    async (email: string, redirectUrl?: string) => {
      debugLog('Requesting magic link for:', email);

      // Construct redirectUrl if not provided
      const finalRedirectUrl = redirectUrl || `${window.location.origin}/authentication/magic`;

      try {
        const data = await executeGraphQLMutation(
          `
          mutation RequestMagicLink($input: RequestMagicLinkInput!) {
            requestMagicLink(input: $input)
          }
        `,
          {
            input: {
              email,
              redirectUrl: finalRedirectUrl,
            },
          },
        );

        if (data?.requestMagicLink === undefined) {
          throw new Error('Invalid response from server');
        }

        return true;
      } catch (error) {
        debugLog('Request magic link error:', error);

        let message = 'An unexpected error occurred';

        if (error instanceof TypeError && error.message === 'Failed to fetch') {
          message = `Cannot connect to server. Please check if the backend is running.`;
        } else if (error instanceof Error) {
          message = error.message;
        }

        throw new Error(message);
      }
    },
    [executeGraphQLMutation],
  );

  const loginWithMagicLink = useCallback(
    async (token: string) => {
      debugLog('Attempting login with magic link');

      setState((prev) => {
        return {
          ...prev,
          isLoading: true,
          error: null,
        };
      });

      try {
        const data = await executeGraphQLMutation(
          `
          mutation LoginWithMagicLink($token: String!) {
            loginWithMagicLink(token: $token) {
              accessToken
              expiresIn
              user {
                id
                loginName
                displayName
                firstName
                lastName
                email
                role
                customerIds
                locale
              }
            }
          }
        `,
          { token },
        );

        if (!data?.loginWithMagicLink) {
          throw new Error('Invalid response from server');
        }

        handleLoginResponse(data.loginWithMagicLink);
      } catch (error) {
        debugLog('Magic link login catch error:', error);

        let message = 'An unexpected error occurred';

        if (error instanceof TypeError && error.message === 'Failed to fetch') {
          message = `Cannot connect to server. Please check if the backend is running.`;
        } else if (error instanceof Error) {
          message = error.message;
        }

        setState((prev) => {
          if (prev.isLoading) {
            return {
              ...prev,
              isLoading: false,
              error: message,
            };
          }
          return prev;
        });

        throw error;
      }
    },
    [executeGraphQLMutation, handleLoginResponse],
  );

  const logout = useCallback(() => {
    debugLog('Logging out');
    clearStorage();
    setState({
      ...initialState,
      isLoading: false,
    });
  }, []);

  const contextValue = useMemo<AuthContextType>(() => {
    return {
      state,
      login,
      loginWithProvider,
      requestMagicLink,
      loginWithMagicLink,
      logout,
      isAuthenticated: state.isAuthenticated,
      user: state.user,
      accessToken: state.accessToken,
      customerIds: state.user?.customerIds || [],
    };
  }, [state, login, loginWithProvider, requestMagicLink, loginWithMagicLink, logout]);

  // Show loading component while checking for existing session
  if (state.isLoading && LoadingComponent) {
    return <>{LoadingComponent}</>;
  }

  return <AuthContext.Provider value={contextValue}>{children}</AuthContext.Provider>;
};

export const useAuth = (): AuthContextType => {
  const context = useContext(AuthContext);

  if (!context) {
    throw new Error('useAuth must be used inside AuthProvider');
  }

  return context;
};

// For backward compatibility with code that expects keycloak-like interface
export const useKeycloak = () => {
  const auth = useAuth();

  // Create a keycloak-like object for backward compatibility
  const keycloak = useMemo(() => {
    return {
      authenticated: auth.isAuthenticated,
      token: auth.accessToken,
      tokenParsed: auth.user
        ? {
            realm_access: {
              roles: [auth.user.role],
            },
            resource_access: {},
            sub: auth.user.id,
            email: auth.user.email,
            preferred_username: auth.user.loginName,
            name: auth.user.displayName,
            given_name: auth.user.firstName || auth.user.displayName.split(' ')[0] || '',
            family_name:
              auth.user.lastName || auth.user.displayName.split(' ').slice(1).join(' ') || '',
          }
        : null,
      profile: auth.user
        ? {
            id: auth.user.id,
            username: auth.user.loginName,
            email: auth.user.email,
            firstName: auth.user.firstName || auth.user.displayName.split(' ')[0] || '',
            lastName:
              auth.user.lastName || auth.user.displayName.split(' ').slice(1).join(' ') || '',
          }
        : null,
      logout: auth.logout,
      // Stub methods that were used from Keycloak
      loadUserProfile: () => {
        return Promise.resolve(
          auth.user
            ? {
                id: auth.user.id,
                username: auth.user.loginName,
                email: auth.user.email,
                firstName: auth.user.firstName || auth.user.displayName.split(' ')[0] || '',
                lastName:
                  auth.user.lastName || auth.user.displayName.split(' ').slice(1).join(' ') || '',
              }
            : null,
        );
      },
      loadUserInfo: () => {
        return Promise.resolve(
          auth.user
            ? {
                sub: auth.user.id,
                email: auth.user.email,
                preferred_username: auth.user.loginName,
                name: auth.user.displayName,
              }
            : null,
        );
      },
    };
  }, [auth.isAuthenticated, auth.accessToken, auth.user, auth.logout]);

  return {
    keycloak,
    initialized: !auth.state.isLoading,
  };
};
